// src/contexts/CanvasActivityContext.tsx
//
// One place that knows what the person just asked for and what it did.
//
// Conversation turns (typed or spoken, through the shell's one assistant) and
// manual commands (a link, a schedule, a removal on any canvas page) report
// here. The provider turns that into:
// - a receipt: what was asked, its save state (saving, saved, partly saved,
//   didn't save), what changed, and Undo when Undo can honestly reverse it;
// - "arrived" ids, so pages can briefly mark the rows that just changed;
// - proposals: things Symphony suggested, held until the person keeps them.
//
// A save is reported only after the data shows it (refetch + diff for agent
// turns, the writer's own result for manual commands).

import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import type { Task } from '@/types/task'
import type { Goal } from '@/types/goal'
import type { Routine } from '@/types/routine'
import type { AgentProposalItem } from '@/lib/agentStream'
import { WRITE_TOOLS } from '@/lib/agentTurn'
import { describeChanges, diffSnapshots, type CanvasChange, type Snapshot } from '@/lib/canvas/changeSet'
import { toolSavingLabel } from '@/lib/canvas/toolLabels'
import { changeCardNote, isQuestion, toApply, type ChangeCardNote, type ChangeSet } from '@/lib/canvas/changeCard'
import type { ApplyResult } from '@/lib/canvas/applyChangeSet'
import { allowanceReceipt, isAllowanceCode } from '@/lib/aiAllowance'

export type SaveState = 'listening' | 'working' | 'saving' | 'saved' | 'partial' | 'failed' | 'answered' | 'unverified'

export interface CanvasReceipt {
  key: number
  source: 'conversation' | 'manual'
  state: SaveState
  /** The server's id for the turn, shown on a failure so a problem report can name it. No content. */
  requestId?: string
  /** What was asked ("Add the plumber to today") or the command's label. */
  request: string
  /** One-line summary of the outcome. */
  summary: string
  changes: CanvasChange[]
  /** Failed operations, by name, when known. */
  failures: string[]
  undoable: boolean
  canRetry: boolean
  /** Number of operations in an explicitly saved review card. */
  changeCount?: number
}

export interface CanvasProposal extends AgentProposalItem {
  state: 'proposed' | 'saving' | 'failed'
}

export interface PendingChangeSet extends ChangeSet {
  state: 'proposed' | 'saving' | 'failed' | 'partial'
  message?: string
  recoverable?: boolean
  /** A newer proposal took the place of one still waiting. */
  replaced?: boolean
}

const PENDING_CARD_KEY = (who: string) => `symphony.pendingCard.${who}`
/** The card this tab had waiting for this person, if any. Shape-checked; a
 *  stale `seen` stamp still makes Approve refuse it if the rows moved. */
export function readPendingCard(who: string): PendingChangeSet | null {
  try {
    const raw = sessionStorage.getItem(PENDING_CARD_KEY(who))
    if (!raw) return null
    const v = JSON.parse(raw) as Partial<PendingChangeSet>
    if (!v || typeof v.key !== 'string' || !Array.isArray(v.ops) || !v.ops.length || typeof v.title !== 'string') return null
    return { ...(v as ChangeSet), state: 'proposed' }
  } catch { return null }
}
export function writePendingCard(who: string, card: PendingChangeSet | null): void {
  try {
    if (card) sessionStorage.setItem(PENDING_CARD_KEY(who), JSON.stringify({ key: card.key, title: card.title, ops: card.ops, ...(card.weekStart ? { weekStart: card.weekStart } : {}) }))
    else sessionStorage.removeItem(PENDING_CARD_KEY(who))
  } catch { /* private mode / blocked site data: the card lives in memory only */ }
}

export interface CanvasWriters {
  deleteTask: (id: string) => Promise<unknown>
  updateTask: (id: string, updates: Partial<Task>) => Promise<unknown>
  deleteGoal: (id: string) => Promise<unknown>
  updateGoal: (id: string, updates: Partial<Goal>) => Promise<unknown>
  deleteRoutine?: (id: string) => Promise<unknown>
  updateRoutine?: (id: string, updates: Partial<Routine>) => Promise<unknown>
}

/** Writes whose rows the canvas holds and can check after a turn. */
const TRACKED_TOOLS = new Set([
  'symphony_create_plan_item', 'symphony_link_plan_item', 'symphony_update_intention',
  'symphony_create_task', 'symphony_update_task', 'symphony_complete_task',
  'symphony_create_routine', 'symphony_update_routine',
])

interface CommandOptions {
  changeCount?: number
  failureState?: 'failed' | 'partial'
  failureSummary?: string
  /** Ids the command touches, marked as arrived once it saves. */
  ids?: string[]
  /** Reverses the command. Offered as Undo only when given. */
  undo?: () => Promise<boolean | void>
  /** Runs the command again, offered when it fails. */
  retry?: () => void
}

export interface CanvasActivity {
  receipt: CanvasReceipt | null
  isArrived: (id: string) => boolean
  arrivedVersion: number
  /** Conversation lifecycle (wired to the shell assistant). */
  turnStarted: (text: string, retry: boolean) => void
  toolUsed: (name: string) => void
  toolResult: (result: { name: string; ok: boolean; ids?: string[]; error?: string }) => void
  turnEnded: (turn: { didWrite: boolean; error: string | null; errorCode?: string; text: string; requestId?: string }) => void
  /** Run a manual command with the same save states and Undo as conversation. */
  run: (label: string, command: () => Promise<boolean | void | string | undefined>, options?: CommandOptions) => Promise<boolean>
  undo: () => Promise<'undone' | 'none' | 'failed'>
  retry: () => void
  dismiss: () => void
  proposals: CanvasProposal[]
  addProposals: (items: AgentProposalItem[]) => void
  setProposalState: (key: string, state: CanvasProposal['state']) => void
  removeProposal: (key: string) => void
  changeSet: PendingChangeSet | null
  setChangeSet: (set: ChangeSet) => void
  dismissChangeSet: () => void
  /** 'questions': every row waits on an answer, so nothing was tried: no
   *  receipt, the last Undo kept, nothing told to the next turn. */
  approveChangeSet: (onResult?: (state: 'saved' | 'partial' | 'failed' | 'questions') => void) => Promise<boolean>
  /** For the next turn: the pending card (every turn while it waits), or
   *  what became of the last one (approved or dismissed), told once. */
  takeChangeCard: () => ChangeCardNote | undefined
  /** What Symphony is pointing at right now; cleared on the next turn. */
  pointing: { dates: Set<string>; taskIds: Set<string> } | null
  setPointing: (p: { dates: string[]; taskIds: string[] }) => void
  /** Whether voice/listening is on (drives the strip's mic state). */
  listening: boolean
  setListening: (on: boolean) => void
}

const CanvasActivityContext = createContext<CanvasActivity | null>(null)

const ARRIVED_MS = 6000
/** How long to wait for refetched rows before diffing what is there. */
const SETTLE_MS = 2500

export interface CanvasActivityProviderProps {
  /** Who is signed in. A card still waiting is kept in this tab's session
   *  storage under it, so an ordinary reload does not lose the proposal
   *  (plan §4). Keyed by person: another sign-in never inherits it. */
  persistKey?: string
  children: ReactNode
  snapshot: Snapshot
  writers: CanvasWriters
  refetch: () => Promise<unknown> | void
  /** Resend the last conversation turn (same turn id). */
  retryTurn?: () => void
  /** Approve on the change card (the shell wires the task writers). */
  applyChanges?: (set: ChangeSet) => Promise<ApplyResult>
}

export function CanvasActivityProvider({ children, snapshot, writers, refetch, retryTurn, applyChanges, persistKey }: CanvasActivityProviderProps) {
  const [receipt, setReceipt] = useState<CanvasReceipt | null>(null)
  const [arrived, setArrived] = useState<Map<string, number>>(new Map())
  const [arrivedVersion, setArrivedVersion] = useState(0)
  const [proposals, setProposals] = useState<CanvasProposal[]>([])
  const [listening, setListening] = useState(false)
  const [pointing, setPointingState] = useState<{ dates: Set<string>; taskIds: Set<string> } | null>(null)
  const setPointing = useCallback((p: { dates: string[]; taskIds: string[] }) => setPointingState(p.dates.length || p.taskIds.length ? { dates: new Set(p.dates), taskIds: new Set(p.taskIds) } : null), [])
  const keyRef = useRef(0)
  const snapshotRef = useRef(snapshot)
  const writersRef = useRef(writers)
  // Latest snapshot and writers for callbacks that outlive a render. Layout
  // effects run before any effect that reads them (finishDiff below).
  useLayoutEffect(() => { snapshotRef.current = snapshot; writersRef.current = writers })
  const undoRef = useRef<(() => Promise<boolean | void>) | null>(null)
  const retryRef = useRef<(() => void) | null>(null)

  // The turn in flight.
  const turnRef = useRef<{
    key: number; request: string; before: Snapshot; startedAt: number
    wrote: boolean; trackedWrite: boolean; reportedOk: boolean; failures: string[]; reportedIds: string[]; awaitingDiff: boolean; ended?: { error: string | null; text: string }
  } | null>(null)

  const nextKey = () => { keyRef.current += 1; return keyRef.current }

  const markArrived = useCallback((ids: string[]) => {
    if (!ids.length) return
    const until = Date.now() + ARRIVED_MS
    setArrived((prev) => { const next = new Map(prev); ids.forEach((id) => next.set(id, until)); return next })
    setArrivedVersion((v) => v + 1)
  }, [])

  // Expire arrived marks.
  useEffect(() => {
    if (!arrived.size) return
    const soonest = Math.min(...arrived.values())
    const t = setTimeout(() => {
      const now = Date.now()
      setArrived((prev) => new Map([...prev].filter(([, until]) => until > now)))
      setArrivedVersion((v) => v + 1)
    }, Math.max(50, soonest - Date.now()))
    return () => clearTimeout(t)
  }, [arrived])

  const isArrived = useCallback((id: string) => (arrived.get(id) ?? 0) > Date.now(), [arrived])

  const turnStarted = useCallback((text: string, retry: boolean) => {
    const key = nextKey()
    turnRef.current = { key, request: text, before: snapshotRef.current, startedAt: Date.now() - 2000, wrote: false, trackedWrite: false, reportedOk: false, failures: [], reportedIds: [], awaitingDiff: false }
    // A question or clarification does not invalidate the last saved change.
    // A new write replaces that Undo, not merely starting a conversation turn.
    setPointingState(null)
    retryRef.current = retryTurn ?? null
    setReceipt({ key, source: 'conversation', state: 'working', request: text, summary: retry ? 'Trying again…' : 'Working on it…', changes: [], failures: [], undoable: false, canRetry: false })
  }, [retryTurn])

  const toolUsed = useCallback((name: string) => {
    const turn = turnRef.current
    if (!turn || !WRITE_TOOLS.has(name)) return
    undoRef.current = null
    turn.wrote = true
    if (TRACKED_TOOLS.has(name)) turn.trackedWrite = true
    setReceipt((r) => r && r.key === turn.key ? { ...r, state: 'saving', summary: toolSavingLabel(name) } : r)
  }, [])

  const toolResult = useCallback((result: { name: string; ok: boolean; ids?: string[]; error?: string }) => {
    const turn = turnRef.current
    if (!turn) return
    if (result.ok) { turn.reportedOk = true; turn.reportedIds.push(...(result.ids ?? [])); markArrived(result.ids ?? []) }
    else turn.failures.push(toolSavingLabel(result.name).replace(/…$/, ''))
  }, [markArrived])

  const finishDiff = useCallback(() => {
    const turn = turnRef.current
    if (!turn || !turn.awaitingDiff) return
    turn.awaitingDiff = false
    const changes = diffSnapshots(turn.before, snapshotRef.current, turn.startedAt)
    markArrived(changes.filter((c) => c.kind !== 'removed').map((c) => c.id))
    const error = turn.ended?.error ?? null
    const failed = turn.failures.length > 0 || !!error
    // Nothing visible changed: a failure only when the write was one this
    // canvas can see. A note, contact or event saved elsewhere is reported as
    // saved when the server confirmed it, otherwise as not checked here.
    const state: SaveState = changes.length
      ? (failed ? 'partial' : 'saved')
      : failed ? 'failed'
        : turn.trackedWrite ? 'failed'
          : turn.wrote ? (turn.reportedOk ? 'saved' : 'unverified')
            : 'answered'
    const undoable = false // Only an attributed server receipt may offer Undo.
    const summary = state === 'answered' ? ''
      : state === 'unverified' ? "Symphony says it's done. It isn't shown on this page, so it wasn't checked here."
      : state === 'saved' && !changes.length ? 'Saved'
      : state === 'failed'
      ? (turn.wrote && !error && !turn.failures.length ? 'Nothing changed. The request may not have saved.' : "Didn't save. Nothing changed.")
      : state === 'partial'
        ? `${describeChanges(changes)}. Some of it didn't save.`
        : describeChanges(changes)
    undoRef.current = null
    setReceipt({ key: turn.key, source: 'conversation', state, request: turn.request, summary, changes, failures: turn.failures, undoable, canRetry: failed && !!retryRef.current })
  }, [markArrived])

  // After a writing turn ends, the refetched rows arrive as a new snapshot.
  useEffect(() => { if (turnRef.current?.awaitingDiff && turnRef.current.ended) finishDiff() }, [snapshot, finishDiff])

  const turnEnded = useCallback((ended: { didWrite: boolean; error: string | null; errorCode?: string; text: string; requestId?: string }) => {
    const turn = turnRef.current
    if (!turn) return
    turn.ended = { error: ended.error, text: ended.text }
    if (ended.requestId) setReceipt((r) => r && r.key === turn.key ? { ...r, requestId: ended.requestId } : r)
    if (!ended.didWrite && !turn.wrote) {
      setReceipt((r) => r && r.key === turn.key
        // The allowance is not a connection failure, and retrying can't help
        // until it resets.
        ? isAllowanceCode(ended.errorCode)
          ? { ...r, state: 'failed', undoable: !!undoRef.current, summary: allowanceReceipt(ended.errorCode), canRetry: false }
          : { ...r, state: ended.error ? 'failed' : 'answered', undoable: !!undoRef.current, summary: ended.error ? "Couldn't reach Symphony. Nothing changed." : '', canRetry: !!ended.error && !!retryRef.current }
        : r)
      return
    }
    turn.awaitingDiff = true
    void Promise.resolve(refetch()).finally(() => setTimeout(finishDiff, SETTLE_MS))
  }, [refetch, finishDiff])

  const run = useCallback(async (label: string, command: () => Promise<boolean | void | string | undefined>, options?: CommandOptions) => {
    const key = nextKey()
    turnRef.current = null
    undoRef.current = null
    retryRef.current = options?.retry ?? null
    setReceipt({ key, source: 'manual', state: 'saving', request: label, summary: 'Saving…', changes: [], failures: [], undoable: false, canRetry: false, changeCount: options?.changeCount })
    let ok = false
    try {
      const result = await command()
      ok = result !== false
    } catch { ok = false }
    if (ok) {
      markArrived(options?.ids ?? [])
      undoRef.current = options?.undo ?? null
    }
    setReceipt((r) => r && r.key === key ? {
      ...r, state: ok ? 'saved' : options?.failureState ?? 'failed',
      summary: ok ? 'Saved' : options?.failureSummary ?? "Didn't save. Nothing changed.",
      undoable: ok && !!options?.undo, canRetry: !ok && !!options?.retry,
    } : r)
    return ok
  }, [markArrived])

  const undo = useCallback(async () => {
    const fn = undoRef.current
    if (!fn) return 'none' as const
    undoRef.current = null
    const key = receipt?.key
    setReceipt((r) => r && r.key === key ? { ...r, state: 'saving', summary: 'Undoing…', undoable: false } : r)
    try {
      const ok = await fn()
      // false: some of it was put back and some was not (a row someone else
      // changed since is left as they left it), so never "nothing changed".
      setReceipt((r) => r && r.key === key ? { ...r, state: ok === false ? 'failed' : 'saved', summary: ok === false ? "Couldn't undo all of it. Check the page." : 'Undone', changes: ok === false ? r.changes : [] } : r)
      void refetch()
      return ok === false ? 'failed' as const : 'undone' as const
    } catch (error) {
      const recoverable = error instanceof Error && error.name === 'UndoOutcomeUnknown'
      if (recoverable) undoRef.current = fn
      setReceipt((r) => r && r.key === key ? { ...r, state: 'failed', undoable: recoverable, summary: recoverable ? "Couldn't confirm Undo. Try Undo again to check the same change safely." : "Couldn't confirm Undo. Check the page." } : r)
      return 'failed' as const
    }
  }, [receipt?.key, refetch])

  const retry = useCallback(() => { const fn = retryRef.current; if (fn) fn() }, [])
  const dismiss = useCallback(() => setReceipt(null), [])

  const addProposals = useCallback((items: AgentProposalItem[]) => {
    if (!items.length) return
    setProposals((prev) => {
      const known = new Set(prev.map((p) => p.key))
      return [...prev, ...items.filter((i) => i.key && i.title && !known.has(i.key)).map((i) => ({ ...i, state: 'proposed' as const }))]
    })
  }, [])
  const setProposalState = useCallback((key: string, state: CanvasProposal['state']) => {
    setProposals((prev) => prev.map((p) => p.key === key ? { ...p, state } : p))
  }, [])
  const removeProposal = useCallback((key: string) => setProposals((prev) => prev.filter((p) => p.key !== key)), [])

  const [changeSet, setPending] = useState<PendingChangeSet | null>(() => persistKey ? readPendingCard(persistKey) : null)
  const pendingRef = useRef<PendingChangeSet | null>(null)
  useLayoutEffect(() => { pendingRef.current = changeSet }, [changeSet])
  // The person is often not known at first render (auth still loading), so
  // the stored card is read when the key arrives, and nothing is written for
  // a key that has not been read yet: otherwise the first render's empty state
  // wiped the stored card before it could be restored (found hosted, 2026-10-15).
  const restoredFor = useRef<string | null>(null)
  useLayoutEffect(() => {
    if (!persistKey || restoredFor.current === persistKey) return
    restoredFor.current = persistKey
    if (!pendingRef.current) { const stored = readPendingCard(persistKey); if (stored) setPending(stored) }
  }, [persistKey])
  // Only a card still waiting survives a reload; saving/failed states do not.
  useEffect(() => { if (persistKey && restoredFor.current === persistKey) writePendingCard(persistKey, changeSet) }, [persistKey, changeSet])
  // What became of the last card, until a turn has been told.
  const cardNoteRef = useRef<ChangeCardNote | null>(null)
  const setChangeSet = useCallback((set: ChangeSet) => {
    cardNoteRef.current = null
    setPending((prev) => {
      // The same set re-announced while it is being saved stays saving.
      if (prev && prev.key === set.key && (prev.state === 'saving' || prev.state === 'partial')) return prev
      return { ...set, state: 'proposed', replaced: !!prev && prev.key !== set.key }
    })
  }, [])
  const dismissChangeSet = useCallback(() => {
    const cur = pendingRef.current
    if (cur) cardNoteRef.current = changeCardNote(cur, 'dismissed')
    setPending(null)
  }, [])
  const takeChangeCard = useCallback((): ChangeCardNote | undefined => {
    // A decided card is told once, first. Only Apply with question rows leaves
    // both a note and a card waiting (setChangeSet clears the note otherwise).
    const note = cardNoteRef.current
    if (note) { cardNoteRef.current = null; return note }
    const cur = pendingRef.current
    return cur ? changeCardNote(cur, 'pending') : undefined
  }, [])
  const approvingRef = useRef(false)
  const approveChangeSet = useCallback(async (onResult?: (state: 'saved' | 'partial' | 'failed' | 'questions') => void) => {
    const set = changeSet
    if (set?.state === 'partial' && !set.recoverable) { onResult?.('partial'); return false }
    if (approvingRef.current || !set || set.state === 'saving' || !applyChanges) return false
    if (!toApply(set.ops).length) { onResult?.('questions'); return false }
    approvingRef.current = true
    try {
    setPending({ ...set, state: 'saving', message: undefined })
    // Rows with a question are not written (applyChangeSet), so not counted.
    const opts: CommandOptions = { ids: [], changeCount: toApply(set.ops).length }
    let failure: string | null = null
    let recoverable = false
    const ok = await run(set.title, async () => {
      const r = await applyChanges(set)
      if (!r.ok) {
        recoverable = r.reason === 'failed' && !!r.recoverable
        failure = r.reason === 'stale'
          ? `“${r.title}” changed since I proposed this. Ask me for a fresh list.`
          : r.reverted
            ? "Couldn't save. Nothing was changed. Try again."
            : recoverable ? "The connection interrupted Save. Check save to recover the same changes safely." : "Couldn't finish, and some of it stayed saved. Check the page before trying again."
        opts.failureState = r.reason === 'failed' && !r.reverted ? 'partial' : 'failed'
        opts.failureSummary = failure
        return false
      }
      opts.ids = r.ids
      opts.undo = r.undo
      return true
    }, opts)
    onResult?.(ok ? 'saved' : opts.failureState ?? 'failed')
    if (ok) cardNoteRef.current = changeCardNote(set, 'approved')
    // Only touch the card if it is still the one that was saved; a newer set
    // that arrived meanwhile stays as it is.
    // Rows with a question stay open, on their own, until they are answered.
    const asked = set.ops.filter(isQuestion)
    setPending((cur) => cur && cur.key === set.key
      ? (ok ? (asked.length ? { ...cur, ops: asked, state: 'proposed', message: undefined, recoverable: undefined, replaced: false } : null)
        : { ...cur, state: opts.failureState ?? 'failed', recoverable, message: failure ?? "Couldn't save. Nothing was changed. Try again." })
      : cur)
    return ok
    } finally { approvingRef.current = false }
  }, [changeSet, applyChanges, run])

  const value = useMemo<CanvasActivity>(() => ({
    receipt, isArrived, arrivedVersion, turnStarted, toolUsed, toolResult, turnEnded, run, undo, retry, dismiss,
    proposals, addProposals, setProposalState, removeProposal, changeSet, setChangeSet, dismissChangeSet, approveChangeSet, takeChangeCard, listening, setListening, pointing, setPointing,
  }), [receipt, isArrived, arrivedVersion, turnStarted, toolUsed, toolResult, turnEnded, run, undo, retry, dismiss, proposals, addProposals, setProposalState, removeProposal, changeSet, setChangeSet, dismissChangeSet, approveChangeSet, takeChangeCard, listening, pointing, setPointing])

  return <CanvasActivityContext.Provider value={value}>{children}</CanvasActivityContext.Provider>
}

const NOOP_ACTIVITY: CanvasActivity = {
  receipt: null, isArrived: () => false, arrivedVersion: 0,
  turnStarted: () => {}, toolUsed: () => {}, toolResult: () => {}, turnEnded: () => {},
  run: async (_l, command) => { try { return (await command()) !== false } catch { return false } },
  undo: async () => 'none', retry: () => {}, dismiss: () => {},
  proposals: [], addProposals: () => {}, setProposalState: () => {}, removeProposal: () => {},
  changeSet: null, setChangeSet: () => {}, dismissChangeSet: () => {}, approveChangeSet: async () => false, takeChangeCard: () => undefined,
  pointing: null, setPointing: () => {},
  listening: false, setListening: () => {},
}

/** The shell's canvas activity. Outside the shell (tests, the wall) commands
 *  still run, without receipts. */
export function useCanvasActivity(): CanvasActivity {
  return useContext(CanvasActivityContext) ?? NOOP_ACTIVITY
}

/** True for a few seconds after the row with this id was created or changed. */
export function useArrived(id: string | null | undefined): boolean {
  const { isArrived } = useCanvasActivity()
  return !!id && isArrived(id)
}
